// Speed <-> pace helpers.
// Pace is always displayed in min/km (matches the sample report).

const MPH_TO_KPH = 1.609344;

/**
 * Convert a numeric speed between units. Returns a string rounded to 1 decimal,
 * or "" when the input is blank/invalid.
 */
export function convertSpeed(value, fromUnit, toUnit) {
  if (fromUnit === toUnit) return value;
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const converted =
    fromUnit === "mph" && toUnit === "kph"
      ? n * MPH_TO_KPH
      : fromUnit === "kph" && toUnit === "mph"
        ? n / MPH_TO_KPH
        : n;
  // keep 1 decimal place, drop trailing .0
  const rounded = Math.round(converted * 10) / 10;
  return String(rounded);
}

/**
 * Convert a speed-range string like "4.9-5.9", "<4.8", "7.5+", "4.9 – 5.9" between units.
 * Returns "" for unparseable input. Keeps the same operator/format.
 */
export function convertSpeedRange(range, fromUnit, toUnit) {
  if (fromUnit === toUnit) return range;
  if (!range || typeof range !== "string") return range;
  const cleaned = range.replace(/\s/g, "").replace(/–|—/g, "-");
  const fmt = (v) => String(Math.round(v * 10) / 10);
  const conv = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return fromUnit === "mph" && toUnit === "kph"
      ? n * MPH_TO_KPH
      : n / MPH_TO_KPH;
  };
  if (cleaned.startsWith("<")) {
    const n = conv(cleaned.slice(1));
    return n == null ? range : `<${fmt(n)}`;
  }
  if (cleaned.endsWith("+")) {
    const n = conv(cleaned.slice(0, -1));
    return n == null ? range : `${fmt(n)}+`;
  }
  if (cleaned.includes("-")) {
    const [a, b] = cleaned.split("-");
    const na = conv(a);
    const nb = conv(b);
    if (na == null || nb == null) return range;
    return `${fmt(na)}-${fmt(nb)}`;
  }
  const n = conv(cleaned);
  return n == null ? range : fmt(n);
}

/**
 * Convert a numeric speed to a pace string "M:SS" (min/km).
 * Returns "" if speed is falsy / non-positive / not a number.
 */
export function speedToPace(speed, unit = "mph") {
  const s = Number(speed);
  if (!Number.isFinite(s) || s <= 0) return "";
  const kph = unit === "kph" ? s : s * MPH_TO_KPH;
  const minutesPerKm = 60 / kph;
  const whole = Math.floor(minutesPerKm);
  const secs = Math.round((minutesPerKm - whole) * 60);
  // handle seconds rounding overflow
  if (secs === 60) return `${whole + 1}:00`;
  return `${whole}:${String(secs).padStart(2, "0")}`;
}

/**
 * Parse a pasted chunk from a spreadsheet (TSV / CSV / mixed whitespace).
 * Column order expected: Time, Pace, Speed, HR, Blood Lactate, RPE.
 * Missing trailing columns are allowed; empty cells are returned as "".
 * The first row is auto-dropped if it looks like a header (non-numeric time).
 */
export function parsePastedRows(text) {
  if (!text) return [];
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const parsed = lines.map((line) => {
    // split on tab first (Excel/Sheets default); fall back to comma; then runs of 2+ spaces
    let cells;
    if (line.includes("\t")) cells = line.split("\t");
    else if (line.includes(",")) cells = line.split(",");
    else cells = line.split(/\s{2,}/);
    cells = cells.map((c) => c.trim());
    return {
      time: cells[0] ?? "",
      pace: cells[1] ?? "",
      speed: cells[2] ?? "",
      hr: cells[3] ?? "",
      blood_lactate: cells[4] ?? "",
      rpe: cells[5] ?? "",
    };
  });
  // drop a header row if Time column is non-numeric
  if (parsed.length > 0 && parsed[0].time && Number.isNaN(Number(parsed[0].time))) {
    parsed.shift();
  }
  return parsed;
}
